import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { Project, ProjectPositionInput } from '@/lib/types';

/*
 * ── Manual ordering on /projects ─────────────────────────────────────────────
 *
 * `PATCH /projects/{id}/position` takes an **ANCHOR** — exactly one of
 * `{beforeId}` or `{afterId}` — and never an index, a `sortOrder` or an
 * `orderedIds` array (each of those is a 422 naming the key, the body being
 * `.strict()`).
 *
 * ⚠️ **The anchor is not a stylistic choice and it decides this whole module.**
 * `sort_order` is ONE GLOBAL SEQUENCE, and the reader is looking at a filtered,
 * paged SLICE of it. The server cannot see the filter, so "move this to the
 * top" means two different things on the two sides of the wire. Anchoring makes
 * the question unambiguous: **"move it to the top of what I can SEE" is
 * `{beforeId: <the id of the first row on screen>}`**, and every move this hook
 * sends anchors on a row that is actually rendered.
 *
 * Two consequences worth stating rather than rediscovering:
 *
 *  - reordering is **correct** on a paged or filtered list, because the anchor
 *    is a visible row either way — but a move ONTO ANOTHER PAGE is not
 *    expressible at all, since no row from that page is on screen to anchor on.
 *    `/projects` says so where the rows are; this hook simply cannot express it;
 *  - a move deliberately does **not** stamp `modifiedBy` and does **not** move
 *    `updatedAt` — the displaced projects were not edited — so the Updated
 *    column must not twitch after a drag, and nothing here writes one.
 *
 * The optimistic model is `use-kanban.ts`'s, with one deliberate difference:
 * **rollback re-applies the previous ID ORDER to the rows currently on screen,
 * rather than restoring a snapshot ARRAY.** `/projects` edits every column
 * inline, so a cell save landing during a move would be silently reverted by a
 * whole-array restore — the row would snap back to the value the user had just
 * changed, with a success message still on it. Reordering by id puts the ORDER
 * back and leaves every field alone.
 */

/** A pure rearrangement of the loaded page, applied by the caller's setState. */
export type ProjectOrdering = (rows: Project[]) => Project[];

/**
 * Lift `movedId` out and drop it immediately before (or after) `anchorId`.
 *
 * Deliberately by ID and not by index: it runs inside a setState updater, so
 * the array it receives is whatever is on screen at that moment and need not be
 * the one the indices were computed against. If either row has gone, it returns
 * the input **unchanged** rather than guessing a position — a wrong guess here
 * is a list that silently disagrees with the server about the stored order.
 */
export function reinsertByAnchor(
  rows: Project[],
  movedId: number,
  anchorId: number,
  before: boolean,
): Project[] {
  if (movedId === anchorId) return rows;
  const from = rows.findIndex((row) => row.id === movedId);
  const anchorAt = rows.findIndex((row) => row.id === anchorId);
  if (from === -1 || anchorAt === -1) return rows;

  const next = rows.slice();
  const [moved] = next.splice(from, 1);
  if (!moved) return rows;
  // Re-found AFTER the removal: taking `anchorAt` across the splice is off by
  // one for every move that travels downwards.
  const target = next.findIndex((row) => row.id === anchorId);
  next.splice(before ? target : target + 1, 0, moved);
  return next;
}

/**
 * Put `rows` back into `ids` order, keeping each row object as it now stands.
 *
 * Returns the input untouched when the two no longer describe the same set — a
 * refetch landed mid-move, so the pre-move order is a statement about rows that
 * are no longer here. The caller reloads instead; see `commit`.
 */
export function restoreOrder(rows: Project[], ids: readonly number[]): Project[] {
  if (rows.length !== ids.length) return rows;
  const byId = new Map(rows.map((row) => [row.id, row]));
  const next: Project[] = [];
  for (const id of ids) {
    const row = byId.get(id);
    if (!row) return rows;
    next.push(row);
  }
  return next;
}

/**
 * Human copy for a failed move.
 *
 * A 422 reports at the key that was sent, and this body carries exactly one of
 * two — so both are read rather than assuming which anchor was used. A 403
 * carries no `details` at all, so it can only ever be a banner; the caller
 * supplies its sentence, because `denialReason` needs the ability object and
 * this hook has no business branching on a role.
 */
function moveMessage(caught: unknown, forbidden: string): string {
  if (!(caught instanceof ApiError)) {
    return caught instanceof Error ? caught.message : 'Could not move that project. It has been put back.';
  }
  if (caught.status === 403) return forbidden;
  if (caught.status === 404) {
    return 'That project no longer exists — someone deleted it. Reloading the list.';
  }
  return (
    caught.fieldErrors.beforeId ??
    caught.fieldErrors.afterId ??
    caught.formMessage ??
    caught.message
  );
}

export interface UseProjectOrderOptions {
  /** The loaded page, in the order it is rendered. */
  rows: Project[];
  /** Applies a rearrangement to the page's own state. */
  reorder: (ordering: ProjectOrdering) => void;
  /**
   * ONE gate for the whole feature: the rows on screen must actually BE the
   * manual order, and the reader must be allowed to write. False disables every
   * entry point rather than each call site remembering to check.
   */
  enabled: boolean;
  /** What a 403 says — `denialReason(...)` from the caller. */
  forbiddenMessage: string;
  /** Re-runs the current query, for the two cases nothing local can repair. */
  reload: () => void;
}

export interface UseProjectOrderResult {
  /** The project currently being moved, for a pending affordance. */
  movingId: number | null;
  /** A failed move, already rolled back. */
  moveError: string | null;
  dismissMoveError: () => void;
  /**
   * Drop `id` onto the position `anchorId` currently holds.
   *
   * Both are `number | string` because a drag hands ids back as STRINGS out of
   * `dataTransfer`, and `'4' === 4` is false — an unnormalised same-position
   * guard never fires, and the reducer then splices a row out and back in while
   * a request that expresses nothing is on the wire.
   */
  moveOnto: (id: number | string, anchorId: number | string) => void;
  /** One step up (-1) or down (+1) — the touch and keyboard path. */
  moveBy: (id: number, delta: -1 | 1) => void;
}

export function useProjectOrder({
  rows,
  reorder,
  enabled,
  forbiddenMessage,
  reload,
}: UseProjectOrderOptions): UseProjectOrderResult {
  const [movingId, setMovingId] = useState<number | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  /*
   * ONE ref, and only because the ROLLBACK needs the rows as they stand when
   * the request fails — several renders after the closure that sent it. Every
   * other value is read straight off the render, so the callbacks below are
   * rebuilt with the rows rather than reaching around React for them; they are
   * consumed by inline handlers, so a stable identity buys nothing.
   *
   * Synced in an effect rather than assigned during render: a render-phase ref
   * write is not safe under a bailout, and it is what oxlint's `react(refs)`
   * rule is for. Effects flush before the next interaction can call `commit`.
   */
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  /** One move at a time — a second drag mid-flight would race its own rollback. */
  const inFlight = useRef(false);

  const dismissMoveError = useCallback(() => setMoveError(null), []);

  const commit = useCallback(
    (from: number, to: number) => {
      if (!enabled || inFlight.current) return;

      const current = rows;
      if (from < 0 || to < 0 || from >= current.length || to >= current.length) return;
      /*
       * SAME POSITION: return WITHOUT A REQUEST.
       *
       * `{beforeId: <the row it already sits before>}` expresses nothing — the
       * server answers with the project unchanged, and the optimistic
       * rearrangement would meanwhile splice the row out and back in, which is
       * a render for nothing and a rollback path for nothing. Same rule, and
       * the same reason, as a same-column drop on a kanban board.
       */
      if (from === to) return;

      const moved = current[from];
      const anchor = current[to];
      if (!moved || !anchor) return;

      /*
       * The anchor is always the row currently AT the destination, and the side
       * follows the direction of travel: going up, the moved row takes that
       * row's place and pushes it down (`beforeId`); going down, it lands just
       * past it (`afterId`). Either way it is a row the reader can SEE — which
       * is the whole reason the endpoint takes an anchor rather than an index.
       */
      const before = to < from;
      const body: ProjectPositionInput = before
        ? { beforeId: anchor.id }
        : { afterId: anchor.id };
      const previousIds = current.map((row) => row.id);

      inFlight.current = true;
      setMoveError(null);
      setMovingId(moved.id);
      reorder((live) => reinsertByAnchor(live, moved.id, anchor.id, before));

      api
        .moveProject(moved.id, body)
        /*
         * The response is the moved project, and it is DELIBERATELY not merged
         * back into the row. A position move writes no field a reader can see —
         * not `updatedAt`, not `modifiedBy` — so there is nothing to adopt,
         * while merging a whole record would overwrite an inline cell edit that
         * landed while this was in flight. The local order is the whole result.
         */
        .then(() => {
          setMoveError(null);
        })
        .catch((caught: unknown) => {
          /*
           * ROLL BACK FIRST, and by ORDER rather than by snapshot — see the
           * module header. If the page is no longer the same SET of rows the
           * order was taken from, `restoreOrder` would hand the array straight
           * back and nothing local can put this right, so the list is reloaded
           * instead of being left quietly wrong. **A move that failed must
           * never be left looking like one that took.**
           */
          const live = rowsRef.current;
          const restorable =
            live.length === previousIds.length &&
            previousIds.every((id) => live.some((row) => row.id === id));
          if (restorable) reorder((current_) => restoreOrder(current_, previousIds));

          setMoveError(moveMessage(caught, forbiddenMessage));
          if (!restorable || (caught instanceof ApiError && caught.status === 404)) reload();
        })
        .finally(() => {
          inFlight.current = false;
          setMovingId(null);
        });
    },
    [rows, reorder, enabled, forbiddenMessage, reload],
  );

  const moveOnto = useCallback(
    (id: number | string, anchorId: number | string) => {
      // Normalised BEFORE anything compares them — a drag hands back strings,
      // and `'4' === 4` is false, so an unnormalised same-position guard would
      // never fire and a request expressing nothing would go out.
      const movedId = Number(id);
      const targetId = Number(anchorId);
      if (!Number.isFinite(movedId) || !Number.isFinite(targetId)) return;
      if (movedId === targetId) return;
      commit(
        rows.findIndex((row) => row.id === movedId),
        rows.findIndex((row) => row.id === targetId),
      );
    },
    [commit, rows],
  );

  const moveBy = useCallback(
    (id: number, delta: -1 | 1) => {
      const from = rows.findIndex((row) => row.id === id);
      if (from === -1) return;
      commit(from, from + delta);
    },
    [commit, rows],
  );

  return { movingId, moveError, dismissMoveError, moveOnto, moveBy };
}
